/**
 * Utilitários para gerenciar templates padrão de mensagens de WhatsApp
 * Contém templates para diferentes tipos de negócio
 */

/**
 * Define os templates padrão de WhatsApp para cada tipo de negócio
 * @param {string} businessType - Tipo de negócio
 * @param {string} businessName - Nome do estabelecimento
 * @returns {Array} - Lista de templates padrão
 */
export const getDefaultTemplatesByBusinessType = (businessType, businessName = 'Estabelecimento') => {
  // Templates padrão com links dinâmicos para todos os tipos de negócio
  const confirmationTemplate = {
    name: "Confirmação de Agendamento",
    type: "confirmacao",
    isDefault: true,
    message: `Olá {cliente},

Seu agendamento para {servico} com {profissional} no dia {data} às {hora} está confirmado!

Para gerenciar seu agendamento, utilize os links abaixo:
✅ *CONFIRMAR*: {link_confirmar}
🗓️ *REAGENDAR*: {link_reagendar}
❌ *CANCELAR*: {link_cancelar}

Agradecemos a preferência!
${businessName}`
  };
  
  const reminderTemplate = {
    name: "Lembrete de Agendamento",
    type: "lembrete",
    isDefault: true,
    message: `Olá {cliente},

Lembramos do seu agendamento para {servico} amanhã ({data}) às {hora} com {profissional}.

Confirme sua presença através do link abaixo:
✅ *CONFIRMAR*: {link_confirmar}
🗓️ *REAGENDAR*: {link_reagendar}
❌ *CANCELAR*: {link_cancelar}

Qualquer imprevisto, por favor, avise-nos com antecedência.
${businessName}`
  };

  // Templates básicos que serão personalizados para cada tipo de negócio
  const baseTemplates = {
    // Template de agradecimento (ajustado por tipo de negócio)
    thankYou: {
      name: 'Agradecimento',
      type: 'agradecimento',
      message: `Olá {{cliente}}, agradecemos sua visita hoje!

Foi um prazer atendê-lo(a). Esperamos que tenha gostado do atendimento.
${businessName}`,
      description: 'Template padrão para agradecimento após atendimento',
      isDefault: false
    },
    
    // Template de reagendamento (ajustado por tipo de negócio)
    reschedule: {
      name: 'Reagendamento',
      type: 'reagendamento',
      message: `Olá {{cliente}}, seu agendamento para {{servico}} foi remarcado para o dia {{data}} às {{hora}}. 

Em caso de dúvidas, entre em contato conosco.
${businessName}`,
      description: 'Template padrão para reagendamento',
      isDefault: false
    },
    
    // Template de cancelamento (ajustado por tipo de negócio)
    cancellation: {
      name: 'Cancelamento',
      type: 'cancelamento',
      message: `Olá {{cliente}}, seu agendamento para {{servico}} no dia {{data}} às {{hora}} foi cancelado conforme solicitado.

Esperamos atendê-lo(a) em breve.
${businessName}`,
      description: 'Template padrão para cancelamento de agendamento',
      isDefault: false
    },
    
    // Template de aniversário (ajustado por tipo de negócio)
    birthday: {
      name: 'Feliz Aniversário',
      type: 'aniversario',
      message: `Olá {{cliente}}, toda a equipe do ${businessName} deseja um Feliz Aniversário! 🎉🎂 

Como presente especial, temos um desconto para você em seu próximo agendamento. 

Agende agora mesmo: {{link}}`,
      description: 'Template padrão para felicitação de aniversário',
      isDefault: false
    },
    
    // Template de promoção (ajustado por tipo de negócio)
    promotion: {
      name: 'Promoção',
      type: 'promocao',
      message: `Olá {{cliente}}, temos uma promoção especial para você!

Aproveite nossos serviços com preços especiais. Entre em contato para saber mais.

Agende agora: {{link}}

${businessName}`,
      description: 'Template padrão para promoções',
      isDefault: false
    }
  };
  
  // Templates específicos por tipo de negócio
  switch (businessType) {
    case 'salao':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];

    case 'estetica':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após procedimento",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita à ${businessName}. Foi um prazer cuidar de você hoje! 

Para manter os resultados, não se esqueça das recomendações pós-procedimento.

Até seu próximo agendamento!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela clínica",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção de tratamentos",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos pacotes promocionais especiais para você!

Até o final do mês, estamos com 15% de desconto em nossos tratamentos faciais e corporais.

Agende seu horário e cuide de você com condições especiais!

${businessName}`
        }
      ];

    case 'barbearia':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após atendimento",
          type: "agradecimento",
          isDefault: true,
          message: `E aí, {cliente}! 

Valeu pela visita à ${businessName} hoje. Foi massa te atender! 

Esperamos que tenha curtido o resultado. Quando precisar dar aquele trato no visual novamente, é só chegar!

Até a próxima!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela barbearia",
          type: "cancelamento",
          isDefault: true,
          message: `Fala, {cliente}!

Infelizmente vamos precisar cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para marcar um novo horário, é só entrar em contato.

Valeu pela compreensão,
${businessName}`
        },
        {
          name: "Promoção de serviços",
          type: "promocao",
          isDefault: true,
          message: `E aí, {cliente}!

Chegou a promoção que você esperava!

Terça e quarta com 15% de desconto em todos os serviços.

Agende seu horário e venha ficar na régua!

${businessName}`
        }
      ];

    case 'podologia':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];
    
    case 'esmalteria':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];
    
    case 'medica':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Orientações pré-consulta",
          type: "lembrete",
          isDefault: false,
          message: `Prezado(a) {cliente},

Lembramos que sua consulta está agendada para {data} às {hora} com Dr(a). {profissional}.

Por favor, chegue com 15 minutos de antecedência e traga seus exames anteriores e lista de medicamentos em uso.

Caso precise cancelar ou remarcar, utilize os links abaixo:
✅ *CONFIRMAR*: {link_confirmar}
🗓️ *REAGENDAR*: {link_reagendar}
❌ *CANCELAR*: {link_cancelar}

Atenciosamente,
${businessName}`
        },
        {
          name: "Cancelamento pela clínica",
          type: "cancelamento",
          isDefault: true,
          message: `Prezado(a) {cliente},

Informamos que precisamos cancelar sua consulta agendada para o dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Entre em contato com nossa recepção para mais informações.

Atenciosamente,
${businessName}`
        }
      ];
    
    case 'spa':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];
    
    case 'pet':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];
    
    case 'tatuagem':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];
    
    case 'odontologica':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Orientações pré-consulta",
          type: "lembrete",
          isDefault: false,
          message: `Olá {cliente},

Sua consulta odontológica está confirmada para {data} às {hora} com Dr(a). {profissional}.

Lembretes importantes:
- Escove bem os dentes antes da consulta
- Traga seus exames recentes (se houver)
- Informe todos os medicamentos que está utilizando

Confirme sua presença:
✅ *CONFIRMAR*: {link_confirmar}
🗓️ *REAGENDAR*: {link_reagendar}
❌ *CANCELAR*: {link_cancelar}

Atenciosamente,
${businessName}`
        },
        {
          name: "Pós-atendimento",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente},

Obrigado por confiar em nossos serviços. Esperamos que tenha tido uma boa experiência em sua consulta.

Lembre-se de seguir todas as recomendações passadas pelo profissional para melhores resultados.

Qualquer dúvida, estamos à disposição.

Atenciosamente,
${businessName}`
        }
      ];
    
    case 'fitness':
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento após visita",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos que tenha gostado do resultado. Se quiser agendar um novo horário, é só entrar em contato!

Até breve!
Equipe ${businessName}`
        },
        {
          name: "Cancelamento pela empresa",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        },
        {
          name: "Promoção",
          type: "promocao",
          isDefault: true,
          message: `Olá {cliente}!

Temos uma promoção especial para você!

Até o final do mês, estamos com 20% de desconto em todos os serviços.

Agende seu horário e aproveite esta oportunidade!

${businessName}`
        }
      ];
    
    // Modelo genérico para outros segmentos
    default:
      return [
        confirmationTemplate,
        reminderTemplate,
        {
          name: "Agradecimento",
          type: "agradecimento",
          isDefault: true,
          message: `Olá {cliente}! 

Muito obrigado pela sua visita ao ${businessName}. Foi um prazer atendê-lo(a)! 

Esperamos vê-lo(a) novamente em breve.

Atenciosamente,
Equipe ${businessName}`
        },
        {
          name: "Cancelamento",
          type: "cancelamento",
          isDefault: true,
          message: `Olá {cliente},

Infelizmente precisamos cancelar seu agendamento para {servico} no dia {data} às {hora}.

Pedimos desculpas pelo inconveniente e gostaríamos de reagendar para uma nova data.

Para agendar um novo horário, por favor entre em contato conosco.

Atenciosamente,
${businessName}`
        }
      ];
  }
};

/**
 * Mapeia o tipo de negócio do formulário para o código interno
 * @param {string} formBusinessType - Tipo de negócio do formulário
 * @returns {string} - Código interno do tipo de negócio
 */
export const mapBusinessTypeToCode = (formBusinessType) => {
  const businessTypeMap = {
    'Salão de Beleza': 'salao',
    'Clínica de Estética': 'estetica',
    'Barbearia': 'barbearia',
    'Podologia': 'podologia',
    'Esmalteria': 'esmalteria',
    'Clínica médica': 'medica',
    'SPA e massagem': 'spa',
    'Pet e Veterinário': 'pet',
    'Estúdio de tatuagem': 'tatuagem',
    'Clínica odontológica': 'odontologica',
    'Personal e fitness': 'fitness',
    'Outros segmentos': 'outros'
  };
  
  return businessTypeMap[formBusinessType] || 'outros';
}; 